/* eslint-disable no-underscore-dangle */
import { UploadFile } from "antd";
import { makeAutoObservable } from "mobx";
import { apiLoadPersonList } from "src/common/apiPerson";
import { makeDictionary } from "src/common/makeDictionary";
import { onError } from "src/common/onError";
import { ZPersonEntity } from "src/types/ZPersonEntity";
import { ZUid } from "src/types/zUid";
import {
  getRoomInfo,
  sendMessage as sendMessageApi,
  sendMessageWithFile as sendMessageWithFileApi,
} from "../apiChat";
import { ZChatInfo } from "../bsChat.types";
import { RCEventListener } from "../rocketChat/RCEventListener";
import {
  rcMessageLoader,
  RCMessageLoaderParams,
} from "../rocketChat/rcMessageLoader";
import {
  RCSChangeEvent,
  ZRCMessage,
  ZRCRoom,
} from "../rocketChat/rocketChat.types";

export type RCFormData = {
  message?: string;
  // описание файла
  description?: string;
  file?: UploadFile;
};

export class ChatFormStore implements RCEventListener {
  chatInfo: ZChatInfo | null = null;

  setChatInfo(info: ZChatInfo) {
    this.chatInfo = info;
  }

  get listenerId() {
    return this.chatInfo?.id || "";
  }

  get initialData(): Partial<ZChatInfo> {
    return this.chatInfo ?? {};
  }

  /**
   * Название чата внутри нашей системы
   */
  get chatName() {
    return this.chatInfo?.name || "";
  }

  get membersCount() {
    return this.chatInfo?.userIds.length || 0;
  }

  roomInfo: ZRCRoom | null = null;

  private setRoomInfo(info: ZRCRoom) {
    this.roomInfo = info;
  }

  loading = false;

  setLoading(flag: boolean) {
    this.loading = flag;
  }

  replyMsgId: ZUid = "";

  savedReplies: Record<string, string> = {};

  setReplyMsgId(id: ZUid) {
    this.replyMsgId = id;
  }

  restoreReplyForChat() {
    this.replyMsgId = this.savedReplies[this.listenerId] || "";
  }

  messageList: ZRCMessage[] = [];

  setMessageList(list: ZRCMessage[]) {
    this.messageList = list;
  }

  /**
   * На данный момент допускаем только обычные сообщения без типа
   */
  get allowedMessages() {
    return this.messageList.filter((msg) => !msg.t).reverse();
  }

  findReplyMsg(msgText: string) {
    const replyId = msgText.match(/\?msg=([^)\n]+)/)?.[1];
    return this.allowedMessages.find((message) => message._id === replyId);
  }

  // пока что пагинация не проработана, загружаем все пачкой, делая такое предположения
  loadParams: RCMessageLoaderParams = { count: 10000, offset: 0 };

  setLoadParams(params: RCMessageLoaderParams) {
    this.loadParams = params;
  }

  personsDict: Record<ZUid, ZPersonEntity> = {};

  setPersonsDict(dict: Record<ZUid, ZPersonEntity>) {
    this.personsDict = dict;
  }

  constructor(info: ZChatInfo, objectId: ZUid) {
    this.setChatInfo(info);
    apiLoadPersonList({
      roleIds: [],
      userIds: this.chatInfo?.userIds,
      objectId,
    })
      .then((data) =>
        this.setPersonsDict(
          makeDictionary(data.content, ({ userId }) => userId),
        ),
      )
      .catch(onError);
    makeAutoObservable(this);
  }

  async init() {
    try {
      this.setLoading(true);
      getRoomInfo(this.listenerId)
        .then((data) => {
          this.setRoomInfo(data);
        })
        .catch(onError);
      this.loadMessages();
    } catch (error) {
      onError(error);
    } finally {
      this.setLoading(false);
    }
  }

  async loadMessages() {
    try {
      const messages = await rcMessageLoader(this.listenerId, this.loadParams);
      this.setMessageList(messages);
    } catch (error) {
      onError(error);
    }
  }

  async sendMessage(data: RCFormData) {
    if (data.file) {
      return sendMessageWithFileApi(
        this.listenerId,
        data.file,
        data.message || "",
      );
    }
    return sendMessageApi(this.listenerId, data.message || "");
  }

  onNewMessage(msgEvent: RCSChangeEvent) {
    if (msgEvent.fields.eventName !== this.listenerId) return;

    let list = [...this.messageList];
    msgEvent.fields.args.forEach((msg) => {
      const idx = list.findIndex((m) => m._id === msg._id);
      if (idx >= 0) {
        // Rocket.Chat шлёт changed повторно после unfurl ссылок — обновляем, не дублируем
        list[idx] = msg;
      } else {
        list = [msg, ...list];
      }
    });
    this.setMessageList(list);
  }
}
